import { Component, EventEmitter, HostBinding, Input, Output } from '@angular/core';

@Component({
    selector: 'vc-common-checkbox',
    templateUrl: './checkbox.component.html',
    styleUrls: ['./checkbox.component.scss'],
})
export class CheckboxComponent<T> {
    /** Whether the checkbox is checked. */
    @HostBinding('class.vc-common-checkbox-checked')
    @Input()
    checked: boolean = false;

    /** Whether the checkbox is indeterminate. This is also known as "mixed" mode and
     *  can be used to represent a checkbox with three states,
     * e.g. a checkbox that represents a nested list of checkable items.
     * Note that whenever checkbox is manually clicked, indeterminate is immediately set to false. */
    @Input()
    set indeterminate(value: boolean) {
        if (value != this._indeterminate) {
            this._indeterminate = value;
            this.indeterminateChange.emit(this._indeterminate);
        }
    }

    get indeterminate(): boolean {
        return this._indeterminate;
    }

    private _indeterminate: boolean = false;

    /** Checkbox label */
    @Input()
    label!: string;

    /** A unique id for the checkbox input. If none is supplied, it will be auto-generated. */
    @Input()
    id!: string;

    /** A unique value for the checkbox */
    @Input()
    value!: T;

    /** The 'aria-describedby' attribute is read after the element's label and field type. */
    @Input()
    ariaDescribedby!: string;

    /** Attached to the aria-label attribute of the host element. In most cases, aria-labelledby will take precedence so this may be omitted. */
    @Input()
    ariaLabel!: string;

    /** Whether the label should appear after or before the checkbox. Defaults to 'after' */
    @Input()
    labelPosition: 'before' | 'after' = 'after';

    /** Whether the checkbox is disabled. This fully overrides the implementation provided by mixinDisabled,
     * but the mixin is still required because mixinTabIndex requires it. */
    @Input()
    disabled: boolean = false;

    /** Whether the checkbox is required. */
    @Input()
    required: boolean = false;

    /** Event is fired when checked changed */
    @Output()
    checkedChange = new EventEmitter<boolean>();

    /** Event is fired when indeterminate changed */
    @Output()
    indeterminateChange = new EventEmitter<boolean>();
}
